import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ReportStatus } from '@dsr/shared-types';
import {
  useDar,
  useUpdateDar,
  useTransitionDar,
  useDeleteDar,
  useDarEntries,
  useCreateDarEntry,
  useDeleteDarEntry,
} from '../api/queries/dars';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { DateInput } from '../components/DateInput';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  StatusBadge,
  Textarea,
} from '../components/ui';
import { fmtDate, fmtDateTime, fmtTime } from '../utils/date';
import { STATUS_LABEL, firstFieldError } from './IncidentsPage';
import './reports.css';

// Allowed transitions a reviewer can pick from, keyed by current status. The
// API enforces the same gating via dar.review.
const NEXT_STATUSES: Record<ReportStatus, ReportStatus[]> = {
  draft: ['submitted'],
  submitted: ['under_review', 'approved', 'rejected'],
  under_review: ['approved', 'rejected'],
  approved: ['under_review'],
  rejected: ['under_review'],
};

export function DARDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();

  const valid = Number.isInteger(id) && id > 0;
  const { data, isPending, error } = useDar(valid ? id : null);
  const entriesQuery = useDarEntries(valid ? id : null);

  const updateDar = useUpdateDar();
  const transitionDar = useTransitionDar();
  const deleteDar = useDeleteDar();
  const createEntry = useCreateDarEntry();
  const deleteEntry = useDeleteDarEntry();

  // ---- Editor state ----
  const [reportDate, setReportDate] = useState<number | null>(null);
  const [shiftStart, setShiftStart] = useState<number | null>(null);
  const [shiftEnd, setShiftEnd] = useState<number | null>(null);
  const [summary, setSummary] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const dar = data?.dar;
  useEffect(() => {
    if (!dar) return;
    setReportDate(dar.report_date);
    setShiftStart(dar.shift_start);
    setShiftEnd(dar.shift_end);
    setSummary(dar.summary ?? '');
  }, [dar]);

  // ---- Entry form ----
  const [showEntry, setShowEntry] = useState(false);
  const [entryAt, setEntryAt] = useState<number | null>(Date.now());
  const [entryCategory, setEntryCategory] = useState('');
  const [entryNote, setEntryNote] = useState('');
  const [entryError, setEntryError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isPending) return <p className="rpt-status-line">Loading report…</p>;
  if (error || !dar) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view this report."
        : error instanceof ApiError && error.status === 404
          ? 'Report not found.'
          : 'Failed to load report.';
    return <p className="rpt-status-line rpt-status-error">{msg}</p>;
  }

  const canWrite = can('dar.write');
  const canReview = can('dar.review');
  const entries = entriesQuery.data?.entries ?? [];

  const onSaveEdit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setEditError(null);
    try {
      await updateDar.mutateAsync({
        id,
        report_date: reportDate,
        shift_start: shiftStart,
        shift_end: shiftEnd,
        summary: summary.trim() || null,
      });
      toast.success('Report updated.');
    } catch (err) {
      setEditError(firstFieldError(err, 'Could not save changes.'));
    }
  };

  const onTransition = async (status: ReportStatus): Promise<void> => {
    try {
      await transitionDar.mutateAsync({ id, status });
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}.`);
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not change status.'));
    }
  };

  const onAddEntry = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setEntryError(null);
    if (!entryNote.trim() && !entryCategory.trim()) {
      setEntryError('Add a category or note for the entry.');
      return;
    }
    try {
      await createEntry.mutateAsync({
        darId: id,
        entry_at: entryAt ?? undefined,
        category: entryCategory.trim() || undefined,
        note: entryNote.trim() || undefined,
      });
      toast.success('Entry added.');
      setShowEntry(false);
      setEntryAt(Date.now());
      setEntryCategory('');
      setEntryNote('');
    } catch (err) {
      setEntryError(firstFieldError(err, 'Could not add the entry.'));
    }
  };

  const onDelete = async (): Promise<void> => {
    try {
      await deleteDar.mutateAsync(id);
      toast.success('Report deleted.');
      navigate('/dars');
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not delete the report.'));
      setConfirmDelete(false);
    }
  };

  return (
    <section className="rpt-page">
      <button type="button" className="rpt-back" onClick={() => navigate('/dars')}>
        ← Back to reports
      </button>

      <header className="rpt-header">
        <div>
          <h2>Daily activity report · {fmtDate(dar.report_date)}</h2>
          <p className="rpt-sub">
            Created {fmtDateTime(dar.created_at)} · review cycle {dar.review_cycle}
          </p>
        </div>
        <div className="rpt-header-actions">
          <StatusBadge status={dar.status}>{STATUS_LABEL[dar.status]}</StatusBadge>
          {canReview &&
            NEXT_STATUSES[dar.status].map((next) => (
              <Button
                key={next}
                variant={next === 'rejected' ? 'danger' : 'primary'}
                onClick={() => onTransition(next)}
                disabled={transitionDar.isPending}
              >
                {next === 'submitted'
                  ? 'Submit'
                  : next === 'under_review'
                    ? 'Start review'
                    : next === 'approved'
                      ? 'Approve'
                      : 'Reject'}
              </Button>
            ))}
          {canWrite && (
            <Button
              variant="danger"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteDar.isPending}
            >
              Delete
            </Button>
          )}
        </div>
      </header>

      <div className="rpt-detail-grid">
        {/* ---- Main column: editor ---- */}
        <div className="rpt-card">
          <h3 className="rpt-card-title">Report details</h3>
          <form className="rpt-form" onSubmit={onSaveEdit}>
            <FormRow columns={3}>
              <FormField label="Report date">
                <DateInput
                  value={reportDate}
                  onChange={setReportDate}
                  disabled={!canWrite || updateDar.isPending}
                />
              </FormField>
              <FormField label="Shift start">
                <DateInput
                  value={shiftStart}
                  onChange={setShiftStart}
                  withTime
                  disabled={!canWrite || updateDar.isPending}
                />
              </FormField>
              <FormField label="Shift end">
                <DateInput
                  value={shiftEnd}
                  onChange={setShiftEnd}
                  withTime
                  disabled={!canWrite || updateDar.isPending}
                />
              </FormField>
            </FormRow>
            <FormField label="Summary">
              <Textarea
                className="rpt-textarea"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={6}
                maxLength={50_000}
                disabled={!canWrite || updateDar.isPending}
              />
            </FormField>
            {editError && (
              <p className="rpt-error" role="alert">
                {editError}
              </p>
            )}
            {canWrite && (
              <div className="rpt-form-actions">
                <Button type="submit" variant="primary" disabled={updateDar.isPending}>
                  {updateDar.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            )}
          </form>
        </div>

        {/* ---- Side column: activity entries ---- */}
        <div className="rpt-detail-side">
          <div className="rpt-card">
            <div className="rpt-section-head">
              <h3>Activity log</h3>
              {canWrite && (
                <button
                  type="button"
                  className="rpt-link"
                  onClick={() => setShowEntry(true)}
                >
                  + Add entry
                </button>
              )}
            </div>
            {entriesQuery.isPending ? (
              <p className="rpt-status-line">Loading…</p>
            ) : entries.length === 0 ? (
              <p className="rpt-empty">No entries logged yet.</p>
            ) : (
              <ul className="rpt-timeline">
                {entries.map((en) => (
                  <li key={en.id} className="rpt-timeline-item">
                    <div className="rpt-timeline-type">
                      {en.category ?? 'Activity'}
                      {canWrite && (
                        <button
                          type="button"
                          className="rpt-link rpt-link-danger"
                          style={{ float: 'right', fontSize: 12 }}
                          onClick={() => deleteEntry.mutate({ darId: id, entryId: en.id })}
                          disabled={deleteEntry.isPending}
                          aria-label="Remove entry"
                        >
                          remove
                        </button>
                      )}
                    </div>
                    <div className="rpt-timeline-time">{fmtTime(en.entry_at)}</div>
                    {en.note && <div className="rpt-timeline-desc">{en.note}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ---- Add-entry modal ---- */}
      <Dialog
        title="Add activity entry"
        open={showEntry}
        busy={createEntry.isPending}
        onClose={() => !createEntry.isPending && setShowEntry(false)}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowEntry(false)}
              disabled={createEntry.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-entry-form"
              variant="primary"
              disabled={createEntry.isPending}
            >
              {createEntry.isPending ? 'Adding…' : 'Add entry'}
            </Button>
          </>
        }
      >
        <form id="add-entry-form" className="rpt-form" onSubmit={onAddEntry}>
          <FormRow columns={2}>
            <FormField label="Time">
              <DateInput
                value={entryAt}
                onChange={setEntryAt}
                withTime
                disabled={createEntry.isPending}
              />
            </FormField>
            <FormField label="Category">
              <Input
                type="text"
                value={entryCategory}
                onChange={(e) => setEntryCategory(e.target.value)}
                placeholder="e.g. patrol, access, alarm, visitor"
                maxLength={120}
                disabled={createEntry.isPending}
              />
            </FormField>
          </FormRow>
          <FormField label="Note">
            <Textarea
              className="rpt-textarea"
              value={entryNote}
              onChange={(e) => setEntryNote(e.target.value)}
              maxLength={20_000}
              disabled={createEntry.isPending}
            />
          </FormField>
          {entryError && (
            <p className="rpt-error" role="alert">
              {entryError}
            </p>
          )}
        </form>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete report"
        message="This soft-deletes the daily activity report and removes it from the active list. Continue?"
        confirmLabel="Delete"
        danger
        busy={deleteDar.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </section>
  );
}
